import React, {Fragment} from 'react';

import {UserAvatar} from 'app/components/general/avatars';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import Tags from 'app/procedures/components/Tags';
import {StyledTeamListItem} from 'app/teams/components/TeamItem';
import {getMembership} from 'app/teams/utils';
import {UserStatusesBadges} from 'app/user/components/UserStatusesBadges';
import {getUserAccountRelation} from 'app/user/utils';
import {gt} from 'app/utils/i18n';

import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

interface Props {
  object: UserSerializedType;
  team: TeamSerializedType;
  canEdit: boolean;
  actionsGetter: any;
  accountOwnerEmail?: string;
}
export function TeamMemberListItem({
  canEdit,
  object: user,
  team,
  accountOwnerEmail,
  actionsGetter,
}: Props) {
  return (
    <StyledTeamListItem
      className={`${user.is_active === false ? 'inactive' : ''}`}
    >
      <div className="col text-truncate">
        <div className="row">
          <div className="col-12 col-lg d-flex align-items-center">
            <UserAvatar
              className="mr-2"
              user={user}
              tooltipPlacement={'right'}
              showCard
            />
            <FullHTMLLink
              html_url={user.html_url}
              title={user.email}
              className="text-dark font-weight-bold"
            >
              {user.name || user.email || gt('Unnamed Person')}{' '}
              {getUserPermDescription(user, accountOwnerEmail, team)}
              <UserStatusesBadges user={user} />
            </FullHTMLLink>
          </div>

          <div className="mt-2 mt-lg-0 col text-truncate d-flex align-items-center">
            <div className="text-truncate">
              <Tags
                items={buildTeamListWithUserPermission(user)}
                getName={TeamName}
                redirectToDashboard={false}
              />
            </div>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="col-auto pl-0 d-flex align-items-center">
          <ThreeDotsDropdownMenu actionsGetter={actionsGetter} object={user} />
        </div>
      )}
    </StyledTeamListItem>
  );
}

type UserTeamMembership = {
  name: string;
  html_url: string;
  isManager?: boolean;
};

export const TeamName = ({
  item: {name, isManager},
}: {
  item: UserTeamMembership;
}) => (
  <span className="text-truncate">
    {isManager === false ? null : (
      <i
        className={`fa manager-icon mr-1 ${
          isManager === true ? 'fa-star' : 'fa fa-circle-notch fa-spin'
        }`}
      />
    )}
    <i className="fa fa-users mr-1" />
    {name}
  </span>
);

export const buildTeamListWithUserPermission = (
  user: UserSerializedType
): Array<
  Pick<UserTeamMembership, 'name' | 'html_url'> & {
    isManager: void | boolean;
  }
> =>
  user.teams.slice(0, 20).map((team) => ({
    name: team.name,
    html_url: team.html_url,
    isManager: getMembership(user, team)?.access == 'manager',
  }));

export const getUserPermDescription = (
  user: UserSerializedType,
  accountOwnerEmail?: string,
  team?: TeamSerializedType
) => {
  if (team) {
    const membership = getMembership(user, team);
    if (membership?.access == 'manager') {
      return <small className="mr-1">{gt('Team Manager')}</small>;
    } else if (membership?.access == 'reader') {
      return <small className="mr-1">{gt('Read Only')}</small>;
    } else {
      return null;
    }
  }

  const perm = getUserAccountRelation(user, accountOwnerEmail);
  return (
    <Fragment>
      {!perm.regular ? <small className="mr-1">{perm.name}</small> : null}

      {user.is_billing_admin === true ? (
        <small className="mr-1">{gt('Billing Administrator')}</small>
      ) : null}
    </Fragment>
  );
};
